import { Config } from 'config';

import { forEach } from 'lodash';

import { deg2rad, rad2deg, OwnedObject, invalidateOwner, resolve, if$, Magnitude } from 'gear';

import { Point, Matrix, Bounds, Frame } from '../geometry';
import { Xml } from '../util';
import { Color } from './color';
import { Rgb } from '../util';

export const BrushBase = OwnedObject.extend({ typeName: 'BrushBase', __debugProps: true });

export function isBrushVisible( brush ) {
  return ( brush !== null && brush !== undefined && !brush.empty() && 
  ( 
    ( brush instanceof Cx.Brush && brush.color !== undefined && !brush.color.showThrough && brush.color.alpha > 0 ) //solid brush
    || ( brush instanceof Cx.TextureBrush ) )  //texture brush
  );
}

// Brush
//===================================================================================================
// A solid brush

export const Brush = BrushBase.extend({ typeName: 'Brush',

  type: 'Brush',

  Properties: {
    color     : { type: 'Color', def: Color.Black(), owned: true, onChange:invalidateOwner },
    materialId: { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    name      : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag       : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label     : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner }
  },

  init( c ) {
    this.callParent(c);
    // Direct color shorcut
    if( c && c.color === undefined ) { this._color = new Color(c); }
  },

  empty() {
    return false;
  },
  _empty: false,
  _type: 'Brush',

  getType() {
    return this._type;
  },

  getColors( colors = [] ) {
    colors.push(this._color);
    return colors;
  },
  setupContext( context/*,transform,frame*/) {
    context.fillStyle = this._color.rgba;
  },
  needsClipping(){ return false; },
  needsFrame() { return false; },
  textualize() {
    // [TODO] Once MaterialId, Name, Tag and Label are supported in the engine,
    // this is the correct textualize function:
    // return textualizeBrushMeta( this, 'Brush: '+ this._color.textualize() );
    // For the moment, same as it is being done in v5
    return 'Brush: '+ this._color.textualize();
  },
  invalidate: invalidateOwner,
  quickToDraw() { return true; },
  needsFixedDrawing() { return false; },
  containsLinkedResources() { return false; }

});

// We use 'Brush' in object types to be able to construct a default brush
// Overwrite the Base construct. We need to review this
Brush.construct = function(v) {
  if( v === null ) {
    return v;
  }
  else if( v instanceof BrushBase ) {
    return v.adopted(this);
  }
  else {
    return ( new Brush(v) ).owner(this);
  }
};

Brush.fromRgb = function(rgb,alpha) { 
  return new Brush({color:Color.fromRgb(rgb,alpha)}); 
};


// transformMode: Fixed, Clamp, RotateOnly, Full, StretchX, StretchY

// Canvas used to draw TextureBrushes
const clippedPatternCanvas = document.createElement('canvas');

export const TextureBrushBase = BrushBase.extend({ typeName: 'TextureBrushBase',

  Properties: {
    textureOffset    : { type: 'Point'   , def: new Point(0,0)                 , dropDef:true, onChange:invalidateOwner },
    targetOffset     : { type: 'Point'   , def: new Point(0,0)                 , dropDef:true, onChange:invalidateOwner },
    matrix22         : { type: 'Matrix22', def: new Matrix()             , dropDef:true, onChange:invalidateOwner },
    transformMode    : { type: 'string'  , def: 'Full'                      , dropDef:true, onChange:invalidateOwner },
    clippingBounds   : { type: 'Bounds'  , def: new Bounds(0,0,1,1)      , dropDef:true, onChange:invalidateOwner },
    colorMap         : { type: 'ColorMap', def: null, owned: true           , dropDef:true, onChange:invalidateOwner },
    materialId       : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    name             : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag              : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label            : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner }
  },

  // _type: '...',

  textualize() {
    const colorMap = this._colorMap;
    return textualizeBrushMeta(this,
      this._type + ': '+
      this._imageLink +' '+
      this._textureOffset.textualize() +' '+
      this._targetOffset.textualize() +' '+
      this._matrix22.textualize() +' '+
      this._transformMode +' '+
      this._clippingBounds.textualize() +
      ( colorMap ? ( ' ' + colorMap.textualize() ) : '' )
    );
  },
  empty() { return false; },
  _empty: false,
  isEqual ( other) { return this.propertiesAreEqual(other); },

  getType() { return this._type; },

  getColors( colors = [] ) {
    const colorMap = this._colorMap;
    if( colorMap ) {
      colorMap.getColors(colors);
    }
    return colors;
  },

  clippingFrame_( renderFrame ) {

    const _this = this, cache = _this._cache,
          id = _this.contentId();

    const info = cache.getInfo(id);
    if( !info ) {
      return Frame.fromBounds(new Bounds(0,0,100,100));
    }

    const clip = this.patternBounds(info.width,info.height);
    const patternWidth = clip.width, patternHeight = clip.height;

    const pt = this.patternTransform(patternWidth,patternHeight,renderFrame);
    const patternBounds = new Bounds(0,0,patternWidth,patternHeight);

    return Frame.fromBounds(patternBounds).transformed(pt);
  },

  patternBounds( patternWidth, patternHeight ) { // pass as params?
    const clip = this._clippingBounds;
    const bounds = new Bounds( Math.floor( patternWidth  * clip.lx ) ,
                               Math.floor( patternHeight * ( 1 - clip.hy ) ) ,
                               Math.ceil(  patternWidth  * clip.hx ) ,
                               Math.ceil(  patternHeight * ( 1 - clip.ly ) ) );

    // Hack require for GDI+, preserved in Canvas so results are exactly the same as
    // the engine. I think we should check if this can be eliminated [TODO]
    // We have added a one pixel guard difference between the actual image and the
    // brush rectangle. It seems that the clipping functions of GDI+ are not working
    // quite right in the borders.
    /*
    if( bounds.lx < 1 ) { bounds.lx = 1; }
    if( bounds.ly < 1 ) { bounds.ly = 1; }
    if( bounds.hx > patternWidth  - 1 ) { bounds.hx = patternWidth  - 1; }
    if( bounds.hy > patternHeight - 1 ) { bounds.hy = patternHeight - 1; }
    */

    return bounds;
  },

  isStretchTransformMode() {
    const transformMode = this._transformMode;
    return transformMode === 'Full'     ||
           transformMode === 'StretchX' ||
           transformMode === 'StretchY' ;
  },

  targetLinealTransform( frame, patternWidth, patternHeight ) {
    const scale = this.computeApplyTransform(frame,patternWidth,patternHeight);
    if( this.isStretchTransformMode() || this._transformMode === 'RotateOnly' ) {
      return Matrix.m( Matrix.rotate(frame.dir_x.angle()), scale ); // [TODO] Check rotation
    }
    else {
      return scale;
    }
  },

  computeApplyTransform( frame, patternWidth, patternHeight ) {
    // This is copied from the C# engine. We could review the steps here because
    // this function is directly called from brush.setupContext.

    // The tiling or stretching is directly controlled by the texture brush lineal
    // transformation. There is no need to do anything in the case of tiling. When
    // stretch is needed, we add a scaling in this direction to account for the
    // needed deformation. In setupContext this transform is going to
    // be applied after the LinealTransform. We need to take this into account here.
    // Stretching in any direction is incompatible with a rotation lineal transform.
    // We would like that the stretching in one direction had no effect over the other
    // but this is not possible due to wich seems to be a bug in GDI+ regarding
    // asymmetrical tiles. The stretching is always performed in only one of the
    // directions mantaining the aspect ratio.

    const transformMode = this._transformMode;
    const matrix22 = this._matrix22;

    let scalex = 1,scaley = 1;
    if( transformMode === 'StretchX' ) {
      scalex = frame.width / ( matrix22.s00 * patternWidth );
      scaley = scalex;
    }
    else if( transformMode === 'StretchY' ) {
      scalex = frame.height / ( matrix22.s11 * patternHeight );
      scaley = scalex;
    }
    else if( transformMode === 'Full' ) {
      scalex = frame.width / ( matrix22.s00 * patternWidth );
      scaley = frame.height / ( matrix22.s11 * patternHeight );
    }

    return Matrix.scale(scalex,scaley);
  },

  targetPos( frame ) {
    return this._transformMode === 'Fixed' ? new Point(0,0) :
                                             frame.interpolate(this._targetOffset);
  },

  targetRenderPos( frame, transformDoc ) {
    return this._transformMode === 'Fixed' ? new Point( transformDoc.dx, transformDoc.dy ) :
                                             frame.interpolate(this._targetOffset);
  },

  // Transform initial bounds to document coordinates ( frame is in doc coordinates )

  patternTransform( patternWidth, patternHeight, frame ) {

    const targetLinealTransform = this.targetLinealTransform(frame,patternWidth,patternHeight);

    const matrix22 = this._matrix22;

    // Create pattern tile
    const textureOffset = this._textureOffset;
    const shift_x = - textureOffset.x * patternWidth  ;
    const shift_y = - textureOffset.y * patternHeight ;

    // Offset and repeat first pattern to base for second pattern
    const t = Matrix.concat( targetLinealTransform,
                             Matrix.scale(1,-1),
                             matrix22,
                             Matrix.translate(shift_x,shift_y) );

    const targetPos = this.targetPos(frame);

    return Matrix.m( Matrix.translate(targetPos), t );
  },

  // [TODO] Clean up, use patternTransform and apply view coordinates afterwards
  // Transform initial bounds to view coordinates ( frame is in view coordinates )
  patternRenderTransform( patternWidth, patternHeight, frame, transformDoc ) {

    const targetLinealTransform = this.targetLinealTransform(frame,patternWidth,patternHeight);

    const matrix22 = this._matrix22;

    // Create pattern tile
    const textureOffset = this._textureOffset;
    const shift_x = - textureOffset.x * patternWidth  ;
    const shift_y = - textureOffset.y * patternHeight ;

    // Offset and repeat first pattern to base for second pattern
    let t = Matrix.concat( targetLinealTransform,
                           Matrix.scale(1,-1),
                           matrix22,
                           Matrix.translate(shift_x,shift_y) );



    if( ! this.isStretchTransformMode() ) {
      t = Matrix.concat( Matrix.scale( transformDoc.scaleFactor() ), t );
    }

    const targetPos = this.targetRenderPos(frame,transformDoc);

    return Matrix.m( Matrix.translate(targetPos), t );
  },

  setupContext( context, transform, frame, transformDoc ) {

    // Obtain image to perform tiling
    const pattern = this.patternImage(transformDoc.scaleFactor(),transform,frame); // Should this already have into account tiling?

    if( ! pattern ) {
      context.fillStyle = 'rgba(0, 100, 0, 0.2)';
      return;
    }

    let image = pattern.image;
    const scale = pattern.scale;

    let patternWidth  = image.width  * scale ,
        patternHeight = image.height * scale ;

    // Clipping support. This is not used that much, we can optimize later
    // The order is important here, textureOffset are computed against
    // the non-clipped pattern in the engine. We may review this, for the moment we are trying
    // to get the exact same result as with the engine
    const clip = this._clippingBounds;
    if( clip.lx !== 0 || clip.ly !== 0 || clip.hx !== 1 || clip.hy !== 1 ) {
      const patternBounds = this.patternBounds(image.width,image.height);
      const clipWidth  = clippedPatternCanvas.width  = patternBounds.width;
      const clipHeight = patternBounds.height;
      if( clippedPatternCanvas.height !== clipHeight ) {
        clippedPatternCanvas.height = clipHeight;
      }
      const clippedPatternContext = clippedPatternCanvas.getContext('2d');
      clippedPatternContext.drawImage(image, patternBounds.lx,patternBounds.ly,clipWidth,clipHeight,
                                             0,0,clipWidth,clipHeight);

      // Use the clipped image as the pattern
      image  = clippedPatternCanvas;
      patternWidth  = image.width  * scale;
      patternHeight = image.height * scale;
    }

    let t = this.patternRenderTransform(patternWidth,patternHeight,frame,transformDoc);

    // Compensate for image quantum

    t = Matrix.m( t, Matrix.scale(scale) );

    // Scheme adapted from ideas from:
    // See http://stackoverflow.com/questions/20253210/canvas-pattern-offset
    // We do not really need to create the tile because we can modify the main context
    // directly. brush.setupContext is expected to be used before context.fill, and after
    // the path has been alredy draw
    context.fillStyle = context.createPattern(image,'repeat');
    t.transformContext(context);

  },

  patternSizeToDraw( zoom, transform, frame, info ) {

    const wpx = info.width,
        hpx = info.height;

    let vw, vh;
    const { transformMode } = this;
    const matrix22_scaleFactor = this._matrix22.maxScaleFactor();

    if( transformMode === 'StretchX' ) {
      vw = frame.width * zoom;
      vh = hpx * vw/wpx;
    }
    else if( transformMode === 'StretchY' ) {
      vh = frame.height * zoom;
      vw = wpx * vh/hpx;
    }
    else if( transformMode === 'Full' ) {
      vh = hpx * frame.height / 450 * zoom * matrix22_scaleFactor;
      vw = wpx * vh/hpx;
    }
    else {
      // Here we will introduce DPI later
      const s = zoom * matrix22_scaleFactor;
      vw = wpx * s;
      vh = hpx * s;
    }

    return { width: vw, height: vh };
  },

  quantumSizeToDraw( zoom,transform,frame,info) {

    const size = this.patternSizeToDraw(zoom,transform,frame,info);
    //if(!this._drawUsingQuantumSize) return Math.floor(Math.max(size.width, size.height));
    let quantumSize = this._cache.sizeToDraw( size.width,size.height,
                                              info.width,info.height);

    // Performing a color map on an image bigger than 512 pixels is too expensive
    const colorMapImageMaxSize = Config.colorMapImageMaxSize;
    if( this._colorMap && quantumSize > colorMapImageMaxSize ) {
      quantumSize = colorMapImageMaxSize;
    }
    return quantumSize;
  },

  preparedToDraw( zoom, transform, frame ) {
    const _this = this, cache = _this._cache,
          colorMap = _this._colorMap,
          id = _this.contentId();

    let quantumSize, image;

    const info = cache.getInfo(id);

    if( info ) {
      quantumSize = _this.quantumSizeToDraw(zoom,transform,frame,info);
      image = cache.get(id,quantumSize,colorMap);
    }
    return !! image;
  },

  prepareToDraw$( zoom, transform, frame, fireRedraw ) {
    const _this = this, cache = _this._cache,
          colorMap = _this._colorMap,
          id = _this.contentId();

    let quantumSize, image;

    const info = cache.getInfo(id);

    if( info ) {
      quantumSize = _this.quantumSizeToDraw(zoom,transform,frame,info);
      image = cache.get(id,quantumSize,colorMap);
    }

    if( image ) {
      return resolve(image);
    }
    else {
      return if$( !info, () => {

        //console.log('asking image info');
        return _withRedraw( cache.info$(id), fireRedraw, _this );

      }) .then( () => {

      const qs = _this.quantumSizeToDraw(zoom,transform,frame, cache.getInfo(id) );

      return _withRedraw( cache.cache$(id,qs,colorMap) ,fireRedraw,_this);

      });
    }
  },

  patternImage( zoom, transform, frame ) {
    const _this = this, cache = this._cache,
          colorMap = _this._colorMap,
          id = _this.contentId(),
          info = cache.getInfo(id);

    let quantumSize, image;

    if( info ) {
      quantumSize = _this.quantumSizeToDraw(zoom,transform,frame,info);
      image = cache.get(id,quantumSize,colorMap);
    }

    if( ! info || ! image ) {

      //console.log('asking image info');

      // We need to use a timeout here to clean up the call stack
      // This is due to Jquery promises, we need to move forward soon
      setTimeout( () => {
        _this.prepareToDraw$(zoom,transform,frame,true);
      },0);

      if( info ) {
        image = cache.getBest(id,quantumSize,colorMap);
      }
    }

    let scale;
    if( image ) {

      // console.log('patternImage: ' + quantumSize + ' ' + image.width + ' ' + image.height);

      scale = // ( 25.4 / _this._dpi ) *
              ( Math.max(info.width,info.height) / Math.max(image.width,image.height) );
    }

    return image ? { image: image, scale: scale } : null;
  },
  get topFigure() {
    const o = this._owner;
    return o && o.topFigure;
  },
  needsClipping(){ return false; },
  needsFrame() {
    return true;
  },
  invalidate: invalidateOwner,
  needsFixedDrawing() {
    return this._transformMode === 'Fixed';
  },
  quickToDraw() {
    return false;
  },
  containsLinkedResources() {
    return true;
  }});
TextureBrushBase.parseProperties = function(node,Type) {
  const p = Xml.parseProperties(node,Type);

  // BP: relLX relLY relWidth relHeight
  const clippingRectString = node.getAttribute('ClippingRect');
  if( ! p.clippingBounds && clippingRectString ) {
    const bounds = Bounds.fromXmlString(clippingRectString);

    // width -> hx, height -> hy
    bounds.hx += bounds.lx;
    bounds.hy += bounds.ly;

    // inverted ly
    const ly = bounds.ly, hy = bounds.hy;
    bounds.ly = 1 - hy;
    bounds.hy = 1 - ly;

    p.clippingBounds = bounds;
  }

  // Backward compatibility
  if( node.getAttribute('AlongX') === 'Stretch' ) {
    p.transformMode = 'StretchX' ;
  }
  if( node.getAttribute('AlongY') === 'Stretch' ) {
    p.transformMode = 'StretchY' ;
  }
  return p;
};


// TextureBrush
//===================================================================================================

const _fireRedraw = function( figure ) {
  return function() {
    const topFigure = figure.topFigure;
    if( topFigure ) {
      topFigure.redraw$();
    }
  };
};
const _withRedraw = function(promise,fireRedraw,figure ) {
  return fireRedraw ? promise.then( _fireRedraw(figure) ) : promise;
};

export const TextureBrush = TextureBrushBase.extend({ typeName: 'TextureBrush',

  type: 'TextureBrush',

  Properties: {
    imageLink : { type: 'string'   , def: null, dropDef:true , onChange:invalidateOwner },
    width     : { type: 'magnitude', def: null, dropDef:true },
    height    : { type: 'magnitude', def: null, dropDef:true },
    dpi       : { type: 'number'   , def: 25.4, tag: 'DPI' }
  },

  _type : 'TextureBrush',

  init( config ) {
    //this._drawUsingQuantumSize = Config.drawUsingQuantumSizeBrush;
    this._cache = Config.QuantumImage;
    this.callParent(config);
  },

  contentId() {
    return this._imageLink;
  }
});
TextureBrush.parseXml = function( node ) {
  const p = TextureBrushBase.parseProperties(node,TextureBrush);
  return new TextureBrush(p);
};
Xml.addFromBuild(TextureBrush,'TextureBrush');
Xml.addToXml(TextureBrush);

// PatternBrush
//===================================================================================================

export const PatternBrush = TextureBrushBase.extend({ typeName: 'PatternBrush',

  type: 'PatternBrush',

  Properties: {
    link   : { type: 'string'   , def: null, dropDef:true, onChange:invalidateOwner },
    width  : { type: 'magnitude', def: null, dropDef:true },
    height : { type: 'magnitude', def: null, dropDef:true }
  },

  _type : 'PatternBrush',

  contentId() {
    return this._link;
  },

  init( config ) {
    //this._drawUsingQuantumSize = Config.drawUsingQuantumSizeBrush;
    this._cache = Config.FiguresCache;
    this.callParent(config);
  }
});
PatternBrush.parseXml = function( node ) {
  const p = TextureBrushBase.parseProperties(node,PatternBrush);
  return new PatternBrush(p);
};
Xml.addFromBuild(PatternBrush,'PatternBrush');
Xml.addToXml(PatternBrush);


// ColorEngineBrush
//===================================================================================================

export const ColorEngineBrush = BrushBase.extend({ typeName: 'ColorEngineBrush',

  type:'ColorEngineBrush',

  Properties: {
    color           : { type: 'Color' , def: Color.White(), owned: true, onChange:invalidateOwner },
    materialId      : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    name            : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag             : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label           : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner }
  },

  textualize() {
    return textualizeBrushMeta( this,
      'ColorEngineBrush: '+
      this._color.textualize()
    );
  },

  empty() { return false; },

  _empty: false,

  _type : 'ColorEngineBrush',

  getType() { return this._type; },
  getColors( colors ) { if(colors===undefined) { colors=[]; }
      colors.push(this._color);
      return colors;
  },
  invalidate: invalidateOwner,
  needsClipping(){ return false; },
  needsFrame() { return true; },
  setupContext( context,transform,frame,transformDoc ) {
  },
  quickToDraw() { return true; },
  needsFixedDrawing() { return false; },
  containsLinkedResources() { return false; }
});
Xml.addFromBuild(ColorEngineBrush,'ColorEngineBrush');
Xml.addToXml(ColorEngineBrush);


// LinearGradientBrush
//===================================================================================================

export const LinearGradientBrush = BrushBase.extend({ typeName: 'LinearGradientBrush',

  type:'LinearGradientBrush',

  Properties: {
    colors          : { type: 'Color[]' , def: [Color.White(),Color.Black()], owned: true, onChange:invalidateOwner },
    positions       : { type: 'number[]', def: [ 0.0, 1.0 ]           , onChange:invalidateOwner },
    from            : { type: 'Point'   , def: new Point(0.0,0.5)        , dropDef:true, onChange:invalidateOwner },
    to              : { type: 'Point'   , def: new Point(1.0,0.5)        , dropDef:true, onChange:invalidateOwner },
    gammaCorrection : { type: 'boolean' , def: true                   , dropDef:true, onChange:invalidateOwner },
    interpolate     : { type: 'boolean' , def: true                   , dropDef:true, onChange:invalidateOwner },
    materialId      : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    name            : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag             : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label           : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner }
  },

  textualize() {
    return textualizeBrushMeta( this,
      'LinearGradientBrush: '+
      this._colors.textualize() +' '+
      this._positions.textualize() +' '+
      this._from.textualize() +' '+
      this._to.textualize() +' '+
      this._gammaCorrection
    );
  },

  empty() { return false; },

  _empty: false,

  _type : 'LinearGradientBrush',

  getType() { return this._type; },
  getColors( colors ) { if(colors===undefined) { colors=[]; }
    const this_colors = this._colors;
    //_.forEach(this_colors,function(c) {
    for(let k=0,kEnd=this_colors.length;k<kEnd;++k) {
      const c = this_colors[k];
      colors.push(c);
    }//);
    return colors;
  },
  invalidate: invalidateOwner,
  needsClipping(){ return false; },
  needsFrame() { return true; },
  setupContext( context,transform,frame,transformDoc ) {
    const fp = this._interpolate ? frame.interpolate(this._from) : transformDoc.apply(this._from),
        tp = this._interpolate ? frame.interpolate(this._to) : transformDoc.apply(this._to);
    const grd = context.createLinearGradient(fp.x,fp.y,tp.x,tp.y);
    const colors = this._colors;
    const positions = this._positions;
    for(let k=0,kEnd=Math.min(colors.length,positions.length);k<kEnd;++k) {
      grd.addColorStop(positions[k], colors[k].rgba);
    }
    context.fillStyle = grd;
  },
  quickToDraw() { return true; },
  needsFixedDrawing() { return false; },
  containsLinkedResources() { return false; }
});
Xml.addFromBuild(LinearGradientBrush,'LinearGradientBrush');
Xml.addToXml(LinearGradientBrush);


// RadialGradientBrush
//===================================================================================================

export const RadialGradientBrush = BrushBase.extend({ typeName: 'RadialGradientBrush',

  type: 'RadialGradientBrush',

  Properties: {
    colors      : { type: 'Color[]' , def: [Color.White(),Color.Black()], owned: true, onChange: invalidateOwner },
    center      : { type: 'Point'   , def: new Point(0.5,0.5), dropDef:false, onChange: invalidateOwner },
    positions   : { type: 'number[]', def: [1.0]          , dropDef:false, onChange: invalidateOwner },
    materialId  : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    name        : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag         : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label       : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    focalPoint  : { type: 'Point' , def: null, dropDef: true, onChange: invalidateOwner }
  },

  textualize() {
    return textualizeBrushMeta( this,
      this._focalPoint ?
      'RadialGradientBrush: '+
      this._colors.textualize()+' '+
      this._center.textualize()+' '+
      this._positions.textualize()+' '+
      this._focalPoint.textualize()
      :
      'RadialGradientBrush: '+
      this._colors.textualize()+' '+
      this._center.textualize()+' '+
      this._positions.textualize()
    );
  },

  init( config ) {
    this.callParent(config);
    if( this._colors.length === 0 ) {
      this.colors = this.Properties.colors.def;
    }
    const rCount = this._colors.length-1;
    if( this._positions.length !== rCount ) {
      const r = [];
      for( let k = 0; k < rCount; ++k ) {
        r[k] = 1.0/(rCount-k) ;
      }
      this.positions = r;
    }
  },

  // [TODO][PROP]
  centerColor( v ) { return v===undefined? this._colors[0]: (this.colors.replaceAt(0,v), this);},
  outerColors() {
    const cs = this._colors;
    return cs.slice(1,cs.length);
  },

  isEqual ( other) { return this.propertiesAreEqual(other); },
  empty() { return false; },
  _empty: false,
  _type : 'RadialGradientBrush',
  getType() { return this._type; },
  getColors( colors = [] ) {
    forEach( this._colors, c => {
      colors.push(c);
    });
    return colors;
  },
  invalidate: invalidateOwner,
  needsClipping(){ return false; },
  needsFrame() { return true; },
  setupContext( context,transform,frame) {
    let lDiagonal = Math.sqrt(frame.width2 + frame.height2);
    const centerShift = this._focalPoint ? this._focalPoint.minus(this._center) : new Point(0,0);
    const cp = frame.interpolate(this._center.minus(centerShift));
    const fp = frame.interpolate(this._focalPoint || this._center);
    const radious = lDiagonal / 2;

    const grd = context.createRadialGradient(fp.x,fp.y,0,cp.x,cp.y,radious);
    const colors = this._colors;
    const positions = this._positions;
    for(let k=0,kEnd=Math.min(colors.length,positions.length+1);k<kEnd;++k) {
      grd.addColorStop(k===0?0:positions[k-1], colors[k].rgba);
    }
    context.fillStyle = grd;
  },
  quickToDraw() {
    return true;
  },
  needsFixedDrawing() {
    return false;
  },
  containsLinkedResources() {
    return false;
  }
});
RadialGradientBrush.parseXml = function( node ) {
  const p = {} ;
  Xml.parseProperties(node,RadialGradientBrush,p);

  p.colors = Color.arrayFromXml(node) ;
  if( p.colors.length === 0 && node.getAttribute('CenterColor') ) {
    // Backward compatibility
    const centerColor = Color.     fromXmlString( node.getAttribute('CenterColor') ) ;
    const outerColors = Color.arrayFromXmlString( node.getAttribute('OuterColors') ) ;
    p.colors = [centerColor].concat(outerColors);
  }
  return new RadialGradientBrush(p) ;
};
Xml.addFromBuild(RadialGradientBrush,'RadialGradientBrush');
Xml.addToXml(RadialGradientBrush);


// LinearHalftoneBrush
//===================================================================================================

export const LinearHalftoneBrush = BrushBase.extend({ typeName: 'LinearHalftoneBrush',

  type:'LinearHalftoneBrush',

  Properties: {
    colors          : { type: 'Color[]'     , def: [Color.White(),Color.Black()], owned: true, onChange:invalidateOwner },
    positions       : { type: 'number[]'    , def: [ 0.0, 1.0 ]                              , onChange:invalidateOwner },
    from            : { type: 'Point'       , def: new Point(0.0,0.5)          , dropDef:true, onChange:invalidateOwner },
    to              : { type: 'Point'       , def: new Point(1.0,0.5)          , dropDef:true, onChange:invalidateOwner },
    maxRadius       : { type: 'magnitude'   , def: new Magnitude(5,'mm')                     , onChange:invalidateOwner },
    hType           : { type: 'string'      , def: 'Ellipse'                                 , onChange:invalidateOwner },
    rotation        : { type: 'number'      , def: 30                                        , onChange:invalidateOwner },
    interleave      : { type: 'boolean'     , def: false                                     , onChange:invalidateOwner },
    wHRatio         : { type: 'number'      , def: 0.25                                      , onChange:invalidateOwner },
    materialId      : { type: 'string'      , def: null                       , dropDef: true, onChange:invalidateOwner },
    name            : { type: 'string'      , def: null                       , dropDef: true, onChange:invalidateOwner },
    tag             : { type: 'string'      , def: null                       , dropDef: true, onChange:invalidateOwner },
    label           : { type: 'string'      , def: null                       , dropDef: true, onChange:invalidateOwner }
  },

  textualize() {
    return textualizeBrushMeta( this,
      'LinearHalftoneBrush: '+
      this._colors.textualize() +' '+
      this._positions.textualize() +' '+
      this._from.textualize() +' '+
      this._to.textualize()
    );
  },

  empty() { return false; },

  _empty: false,

  _type : 'LinearHalftoneBrush',

  getType() { return this._type; },

  getColors( colors ) { if(colors===undefined) { colors=[]; }
    const this_colors = this._colors;
    for(let k=0,kEnd=this_colors.length;k<kEnd;++k) {
      const c = this_colors[k];
      colors.push(c);
    }
    return colors;
  },
  invalidate: invalidateOwner,
  needsClipping(){ return true; },
  needsFrame() { return true; },

  setupContext( aContext, aTransform, aFrame, aTransformDoc) {

     let lfp = aFrame.interpolate(this._from),
           ltp = aFrame.interpolate(this._to);
    const lZoom = aTransformDoc.scaleFactor();
    let lFrameBounds = aFrame.bounds;
    let lCp = aFrame.center;
    let lDiagonal = Math.max(Point.distance(aFrame.p0,aFrame.p2),Point.distance(aFrame.p1,aFrame.p3));

    let lDir = Point.substract(ltp,lfp);
    lDir = lDir.normalized();
    const lOffset = lDir.scalarMultiply(lDiagonal,lDiagonal);
    let lfp_extended = Point.substract(lfp,lOffset);
    let ltp_extended = Point.add(ltp,lOffset);

    this.drawBrush(lfp, ltp, lfp_extended, ltp_extended, lFrameBounds , lZoom, aContext);
  },

  drawBrush(aFp, aTp, aFpExt, aTpExt, aFrameBounds, aZoom, aContext)
  {
    const lColors = this._colors;
    const lPositions = this._positions;

    //this is required to fill whats not inside the gradient range
    this.buildHalftonesFor(0,1,lColors[0],lColors[0],aFpExt,aFp,aFrameBounds,aContext,aZoom);
    this.buildHalftonesFor(0,1,lColors[lColors.length-1],lColors[lColors.length-1],aTpExt,aTp,aFrameBounds,aContext,aZoom);

    for (let i = 1; i < lPositions.length; ++i)
    {
      this.buildHalftonesFor(lPositions[i-1],lPositions[i],lColors[i-1],lColors[i],aFp,aTp,aFrameBounds,aContext,aZoom);
    }
    
  },

  buildHalftonesFor(aSPos, aEPos, aSCol, aECol, aFp, aTp, aFrameBounds, aContext, aZoom )
  {
    const lSPoint = Point.interpolate(aFp,aTp,aSPos);
    const lEPoint = Point.interpolate(aFp,aTp,aEPos);
    const lAxisLen = Point.distance(lSPoint,lEPoint);
    if (lAxisLen > 0.0)
    {
      const lDir = Point.scalarMultiply(Point.substract(lEPoint, lSPoint), 1.0 / lAxisLen);
      const lFillDir = lDir.perpendicular(1);
      const lMaxPolySide = Math.max(aFrameBounds.width, aFrameBounds.height);
      const lXOffset = lFillDir.x * lMaxPolySide;
      const lYOffset = lFillDir.y * lMaxPolySide;
      const lX1 = lSPoint.x + lXOffset;
      const lY1 = lSPoint.y + lYOffset;
      const lX2 = lSPoint.x - lXOffset;
      const lY2 = lSPoint.y - lYOffset;
      const lX3 = lEPoint.x - lXOffset;
      const lY3 = lEPoint.y - lYOffset;
      const lX4 = lEPoint.x + lXOffset;
      const lY4 = lEPoint.y + lYOffset;
      
      aContext.fillStyle = aSCol.rgba;
      aContext.beginPath();
      aContext.moveTo(lX1,lY1);
      aContext.lineTo(lX2,lY2);
      aContext.lineTo(lX3,lY3);
      aContext.lineTo(lX4,lY4);
      aContext.closePath();
      aContext.fill();
      
      const lZoomedMaxRadius = this._maxRadius * aZoom;

      if (lZoomedMaxRadius > 0.0 && aECol != aSCol)
      {
        let lIterations = Math.ceil(lAxisLen/lZoomedMaxRadius);
        lIterations = Math.max(1,lIterations);

        const lSizeDelta = lZoomedMaxRadius / lIterations;

        //we want to end exactly so the last circle is only half rendered
        const lStartOffsetX = lEPoint.x - (lIterations - 1) * lZoomedMaxRadius * lDir.x;
        const lStartOffsetY = lEPoint.y - (lIterations - 1) * lZoomedMaxRadius * lDir.y;

        //we shift up and down so the pattern is interleaved
        let lShiftX = 0;
        let lShiftY = 0;

        if (this._interleave)
        {
          lShiftX = (lFillDir.x * lZoomedMaxRadius) / 4;
          lShiftY = (lFillDir.y * lZoomedMaxRadius) / 4;
        }
        
        let lAngle = lDir.angle() + this._rotation;

        //A x B = 1
        //A / B = ratio
        //B = 1 / A
        //A / ( 1 / A ) = ratio
        //(AxA) = ratio
        //A = sqrt( ratio)
        //B = 1 / A
        this._wHRatio = Math.max(this._wHRatio, 0.1);
        this._wHRatio = Math.min(this._wHRatio, 1.0);
        let lEllipseW = Math.sqrt(this._wHRatio);
        let lEllipseH = 1 / lEllipseW;

        for (let i = 0; i < lIterations; ++i)
        {
          const lX = lStartOffsetX + i * lZoomedMaxRadius * lDir.x + lShiftX;
          const lY = lStartOffsetY + i * lZoomedMaxRadius * lDir.y + lShiftY;
          const lSize = (i + 1) * lSizeDelta;
          this.fillLineWithElements(lX, lY, aFrameBounds, lFillDir, lDir, lSize, lZoomedMaxRadius, lEllipseW, lEllipseH, lAngle, aECol, aContext);
          lShiftX = -lShiftX;
          lShiftY = -lShiftY;
        }
      }
    }
  },

  fillLineWithElements(aX, aY, aFrameBounds, aFillDir, aDir, aSize, aMaxSize, aEW, aEH, aAngle, aECol, aContext)
  {
      let lCurrX = aX;
      let lCurrY = aY;
      let lDx = aFillDir.x * aMaxSize;
      let lDy = aFillDir.y * aMaxSize;

      const hType = this._hType;

      if (hType === 'Ellipse' || hType === 'ParallelEllipse')
      {
        aMaxSize *= 1.5;//the ellipse size grows so to avoid missing some on the borders we increase this
      }
      else if (hType === 'Full')
      {
        let lMaxPolySide = Math.max(aFrameBounds.width, aFrameBounds.height);

        aContext.strokeStyle = aECol.rgba;
        aContext.lineWidth = aSize;
        aContext.beginPath();
        aContext.moveTo(aX+aFillDir.x*lMaxPolySide,aY+aFillDir.y*lMaxPolySide);
        aContext.lineTo(aX-aFillDir.x*lMaxPolySide,aY-aFillDir.y*lMaxPolySide);
        aContext.closePath();
        aContext.stroke();
        return;
      }
      
      while (lCurrX > -aMaxSize + aFrameBounds.lx  && lCurrX < aFrameBounds.hx + aMaxSize && lCurrY > -aMaxSize + aFrameBounds.ly && lCurrY < aFrameBounds.hy + aMaxSize)
      {
        this.drawElement(aSize, aECol, aContext, lCurrX, lCurrY, aEW, aEH, aAngle);

        lCurrX += lDx;
        lCurrY += lDy;
      }

      lCurrX = aX - lDx;
      lCurrY = aY - lDy;

      while (lCurrX > -aMaxSize + aFrameBounds.lx  && lCurrX < aFrameBounds.hx + aMaxSize && lCurrY > -aMaxSize + aFrameBounds.ly && lCurrY < aFrameBounds.hy + aMaxSize)
      {
        this.drawElement(aSize, aECol, aContext, lCurrX, lCurrY, aEW, aEH, aAngle);
        lCurrX -= lDx;
        lCurrY -= lDy;
      }
  },
  drawElement(aSize, aCol, aContext, aCurrX, aCurrY, aEW, aEH, aAngle)
  {
    const hType = this._hType;
    const lRadius = aSize*0.5;
    
    if (hType === 'Circle')
    {
      aContext.fillStyle = aCol.rgba;
      aContext.beginPath();
      aContext.ellipse(aCurrX, aCurrY, lRadius, lRadius, 0, 0, 2*Math.PI);
      aContext.closePath();
      aContext.fill();
    }
    else //if (HType == Ellipse)
    {
      aContext.fillStyle = aCol.rgba;
      aContext.beginPath();
      aContext.ellipse(aCurrX, aCurrY, lRadius*aEW, lRadius*aEH, deg2rad(aAngle), 0, 2*Math.PI);
      aContext.closePath();
      aContext.fill();
    }
  },
  quickToDraw() { return false; },
  needsFixedDrawing() { return false; },
  containsLinkedResources() { return false; }
});
Xml.addFromBuild(LinearHalftoneBrush,'LinearHalftoneBrush');
Xml.addToXml(LinearHalftoneBrush);


// RadialHalftoneBrush
//===================================================================================================

export const RadialHalftoneBrush = BrushBase.extend({ typeName: 'RadialHalftoneBrush',

  type: 'RadialHalftoneBrush',

  Properties: {
    colors      : { type: 'Color[]'   , def: [Color.White(),Color.Black()], owned: true , onChange: invalidateOwner },
    center      : { type: 'Point'     , def: new Point(0.5,0.5), dropDef:false          , onChange: invalidateOwner },
    positions   : { type: 'number[]'  , def: [1.0]          , dropDef:false             , onChange: invalidateOwner },
    maxRadius   : { type: 'magnitude' , def: new Magnitude(5,'mm')                      , onChange: invalidateOwner },
    hType       : { type: 'string'    , def: 'Ellipse'                                  , onChange: invalidateOwner },
    rotation    : { type: 'number'    , def: 30                                         , onChange: invalidateOwner },
    interleave  : { type: 'boolean'   , def: false                                      , onChange: invalidateOwner },
    wHRatio     : { type: 'number'    , def: 0.25                                       , onChange: invalidateOwner },
    materialId  : { type: 'string'    , def: null, dropDef: true                        , onChange: invalidateOwner },
    name        : { type: 'string'    , def: null, dropDef: true                        , onChange: invalidateOwner },
    tag         : { type: 'string'    , def: null, dropDef: true                        , onChange: invalidateOwner },
    label       : { type: 'string'    , def: null, dropDef: true                        , onChange: invalidateOwner }
  },

  textualize() {
    return textualizeBrushMeta( this,
      'RadialHalftoneBrush: '+
      this._colors.textualize()+' '+
      this._center.textualize()+' '+
      this._positions.textualize()
    );
  },

  init( config ) {
    this.callParent(config);
    if( this._colors.length === 0 ) {
      this.colors = this.Properties.colors.def;
    }
    const rCount = this._colors.length-1;
    if( this._positions.length !== rCount ) {
      const r = [];
      for( let k = 0; k < rCount; ++k ) {
        r[k] = 1.0/(rCount-k) ;
      }
      this.positions = r;
    }
  },

  centerColor( v ) { return v===undefined? this._colors[0]: (this.colors.replaceAt(0,v), this);},
  outerColors() {
    const cs = this._colors;
    return cs.slice(1,cs.length);
  },

  isEqual ( other) { return this.propertiesAreEqual(other); },
  empty() { return false; },
  _empty: false,
  _type : 'RadialHalftoneBrush',
  getType() { return this._type; },
  getColors( colors = [] ) {
    forEach( this._colors, c => {
      colors.push(c);
    });
    return colors;
  },
  invalidate: invalidateOwner,
  needsClipping(){ return true; },
  needsFrame() { return true; },
  setupContext( aContext, aTransform, aFrame, aTransformDoc) {
    let lCp = aFrame.interpolate(this._center);
    let lRadius = Math.max(Point.distance(aFrame.p0,lCp),
                           Point.distance(aFrame.p1,lCp),
                           Point.distance(aFrame.p2,lCp),
                           Point.distance(aFrame.p3,lCp));
    const lZoom = aTransformDoc.scaleFactor();
    let lFrameBounds = aFrame.bounds;
    
    this.drawBrush(lCp, lRadius, lFrameBounds , lZoom, aContext);
  },

  drawBrush(aCp, aRadius, aFrameBounds, aZoom, aContext)
  {
    const lCenterColor = this.centerColor();
    const lOuterColors = this.outerColors();
    const lPositions = this._positions;

    const lLast = lPositions.length - 1;
    this.buildHalftonesFor(aCp,
                            lPositions[lLast]*aRadius, 
                            aRadius, 
                            lOuterColors[lLast], 
                            lOuterColors[lLast], 
                            aFrameBounds, 
                            aContext, 
                            aZoom);

    //we render outter to inner, just for convenience
    for (let i = lPositions.length; i > 0; --i)
    {
      this.buildHalftonesFor(aCp,
                              i === 1 ? 0.0 : lPositions[i - 2]*aRadius, 
                              lPositions[i - 1]*aRadius, 
                              i === 1 ? lCenterColor:lOuterColors[i - 2], 
                              lOuterColors[i - 1], 
                              aFrameBounds, 
                              aContext, 
                              aZoom);
    }
    
  },

  buildHalftonesFor(aCenter, aSRadius, aERadius, aSCol, aECol, aFrameBounds, aContext, aZoom )
  {
    const lRadiusLen = aERadius - aSRadius;
    if (lRadiusLen > 0.0)
    {
      aContext.fillStyle = aSCol.rgba;
      aContext.beginPath();
      aContext.ellipse(aCenter.x, aCenter.y, aERadius, aERadius, 0, 0, 2*Math.PI);
      aContext.closePath();
      aContext.fill();
      
      const lZoomedMaxRadius = this._maxRadius * aZoom;
      if (lZoomedMaxRadius > 0.0)
      {
        let lIterations = Math.ceil(lRadiusLen/lZoomedMaxRadius);
        lIterations = Math.max(1,lIterations);

        const lSizeDelta = lZoomedMaxRadius / lIterations;

        //we want to end exactly so the last circle is only half rendered
        const lStartOffset = aERadius - (lIterations - 1) * lZoomedMaxRadius;

        //we shift up and down so the pattern is interleaved
        let lShift = 0;

        if (this._interleave)
        {
          lShift = 0.25;
        }
        
        //A x B = 1
        //A / B = ratio
        //B = 1 / A
        //A / ( 1 / A ) = ratio
        //(AxA) = ratio
        //A = sqrt( ratio)
        //B = 1 / A
        this._wHRatio = Math.max(this._wHRatio, 0.1);
        this._wHRatio = Math.min(this._wHRatio, 1.0);
        let lEllipseW = Math.sqrt(this._wHRatio);
        let lEllipseH = 1 / lEllipseW;

        for (let i = 0; i < lIterations; ++i)
        {
          const lRadius = lStartOffset + i * lZoomedMaxRadius;
          const lSize = (i + 1) * lSizeDelta;
          this.fillCircleWithElements(aCenter, lRadius, lShift, lSize, lZoomedMaxRadius, lEllipseW, lEllipseH, aECol, aContext);
          lShift = -lShift;
        }
      }
    }
  },

  fillCircleWithElements(aCenter, aRadius, aShift, aSize, aMaxSize, aEW, aEH, aECol, aContext)
  {
      const lDelta = Math.floor((Math.PI*2.0*aRadius/aMaxSize));
      if (lDelta > 0)
      {
        const lDeltaAngle = (-360.0 / lDelta);

        let lCurrX = aCenter.x;
        let lCurrY = aCenter.y;

        const hType = this._hType;

        if (hType === 'Full')
        {
          aContext.strokeStyle = aECol.rgba;
          aContext.lineWidth = aSize;
          aContext.beginPath();
          aContext.ellipse(aCenter.x, aCenter.y, aRadius, aRadius, 0, 0, 2*Math.PI);
          aContext.closePath();
          aContext.stroke();
          return;
        }

        lCurrX += aRadius;

        let lMatrix = Matrix.rotateAround(aCenter,aShift*lDeltaAngle);
        lMatrix.transformContext(aContext);

        for (let i = 0; i < lDelta; ++i)
        {
          this.drawElement(aSize, aECol, aContext, lCurrX, lCurrY, aEW, aEH, lDeltaAngle*(i+aShift));
          lMatrix = Matrix.rotateAround(aCenter, lDeltaAngle);
          lMatrix.transformContext(aContext);
        }
        aContext.resetTransform();
      }
  },

  drawElement(aSize, aCol, aContext, aCurrX, aCurrY, aEW, aEH, aAngle)
  {
    const hType = this._hType;
    const lRadius = aSize*0.5;
    
    if (hType === 'Circle')
    {
      aContext.fillStyle = aCol.rgba;
      aContext.beginPath();
      aContext.ellipse(aCurrX, aCurrY, lRadius, lRadius, 0, 0, 2*Math.PI);
      aContext.closePath();
      aContext.fill();
    }
    else //if (HType == Ellipse or ParallelEllipse)
    {
      aContext.fillStyle = aCol.rgba;
      aContext.beginPath();
      let lAngle = -this._rotation;
      if (hType === 'ParallelEllipse')
      {
        lAngle-=aAngle;
      }
      aContext.ellipse(aCurrX, aCurrY, lRadius*aEW, lRadius*aEH, deg2rad(lAngle) , 0, 2*Math.PI);
      aContext.closePath();
      aContext.fill();
    }
  },  quickToDraw() {
    return false;
  },
  needsFixedDrawing() {
    return false;
  },
  containsLinkedResources() {
    return false;
  }
});
RadialHalftoneBrush.parseXml = function( node ) {
  const p = {} ;
  Xml.parseProperties(node,RadialHalftoneBrush,p);

  p.colors = Color.arrayFromXml(node) ;
  if( p.colors.length === 0 && node.getAttribute('CenterColor') ) {
    // Backward compatibility
    const centerColor = Color.     fromXmlString( node.getAttribute('CenterColor') ) ;
    const outerColors = Color.arrayFromXmlString( node.getAttribute('OuterColors') ) ;
    p.colors = [centerColor].concat(outerColors);
  }
  return new RadialHalftoneBrush(p) ;
};
Xml.addFromBuild(RadialHalftoneBrush,'RadialHalftoneBrush');
Xml.addToXml(RadialHalftoneBrush);


Brush.Black  = function() { return new Brush({color:Color.Black()}); };
Brush.White  = function() { return new Brush({color:Color.White()}); };

// EmptyBrush
//===================================================================================================
// The system uses an empty brush object to simplify the implementation.
// We avoid checking for nulls in a lot of cases using this scheme.

export const EmptyBrush = BrushBase.extend({ typeName: 'EmptyBrush',

  type: 'EmptyBrush',

  empty() { return true; },

  _empty: true,
  _type : 'Empty',

  isEqual( other ) {
    return other instanceof EmptyBrush;
  },

  isNear( other/*,eps*/) {
    return other._empty;
  },

  get color() {
    return Color.Transparent();
  },

  getType() {
    return 'Empty';
  },
  textualize() {
    return 'EmptyBrush';
  },

  buildXml() {},
  setupContext() {},
  setupXml() {},
  getColors( colors = [] ) {
    return colors;
  },

  invalidate() {},
  needsClipping(){ return false; },
  needsFrame() {
    return false;
  },
  quickToDraw() {
    return true;
  },
  needsFixedDrawing() {
    return false;
  },
  containsLinkedResources() {
    return false;
  }
});
Xml.addToXml(EmptyBrush);

Brush.empty = new EmptyBrush();
Brush.empty._owner = {};

Brush.def = function(b,def) { return b._empty ? def : b; };

// [TODO] Move each brush to its own source

// Brush.fromXml checks all types of brushes
Brush._fromXml = function( node ) 
{ 
    var brush = Xml.getObject(node,Brush,'Brush'); 
    if ( ( brush !== null ) && ( brush.color.tint < 1 ) )
    {
      var color = new Cx.Color();
      var aux_color = new Cx.Color.fromImageData(Cx.interpolate_rgb( Cx.Color.White().rgbArray, brush.color.rgbArray, brush.color.tint ));
      color.copyProperties(brush.color);
      color.rgb = aux_color.rgb; //copy all properties except from RGB
      brush.color = color;
    }
    return brush ;
};
Brush.fromXml = function( node ) {
  return Brush             ._fromXml(node) ||
         TextureBrush       .fromXml(node) ||
         PatternBrush       .fromXml(node) ||
         LinearGradientBrush.fromXml(node) ||
         RadialGradientBrush.fromXml(node) ||
         LinearHalftoneBrush.fromXml(node) ||
         RadialHalftoneBrush.fromXml(node) ||
         ColorEngineBrush   .fromXml(node) ||
         Brush.empty ;
};
Brush.fromXmlString = function(xmlString) {
  const domFigure = Xml.fromString(xmlString);
  return Brush.fromXml(domFigure.documentElement);
};

Xml.addFromBuild(Brush,'Brush');
Xml.addToXml(Brush);

Xml.Parser['Brush'] = Xml.makeObjectParser(Brush);

function textualizeBrushMeta(brush,s) {
  const { materialId, name, tag, label } = brush;
  if( materialId ) { s += ' M: ' + materialId; }
  if( name ) { s += ' N: ' + name; }
  if( tag ) { s += ' T: ' + tag; }
  if( label ) { s += ' L: ' + label; }
  return s;
}



/** WEBPACK FOOTER **
 ** ./src/cdl/style/brush.js
 **/